import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Coins, Loader2 } from "lucide-react";
import { getCommissionGroups, createCommissionGroup, updateCommissionGroup, deleteCommissionGroup } from "@/lib/employeeApi";
import { useAdminAccess } from "@/lib/adminAccess";

export default function AdminCommissionGroups() {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const canManage = useAdminAccess().can("settings_commissions", "manage");
  const load = useCallback(() => getCommissionGroups().then(setGroups).catch(() => toast.error("Failed to load groups")), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await updateCommissionGroup(form.id, form); else await createCommissionGroup(form);
      toast.success("Commission group saved"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    try { await deleteCommissionGroup(g.id); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-commission-groups">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Commission groups are assigned to employees in Users → Human Resources. Payout rules come later.</p>
        {canManage && <Button onClick={() => setForm({ name: "", description: "" })} className="bg-teal hover:bg-teal-dark text-white" data-testid="create-commission-group-btn"><Plus className="w-4 h-4 mr-2" />New Group</Button>}
      </div>
      {groups.length === 0 && <p className="text-sm text-slate-500" data-testid="commission-groups-empty">No commission groups yet.</p>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <Card key={g.id} className="border-slate-200" data-testid={`commission-group-${g.id}`}>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-center gap-2"><Coins className="w-5 h-5 text-teal" /><p className="font-heading font-semibold text-navy">{g.name}</p></div>
              {g.description && <p className="text-xs text-slate-500">{g.description}</p>}
              <p className="text-xs text-slate-500">{g.user_count} employee(s)</p>
              {canManage && <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setForm({ ...g })} data-testid={`edit-commission-group-${g.id}`}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => remove(g)} data-testid={`delete-commission-group-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>}
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{form?.id ? "Edit Commission Group" : "New Commission Group"}</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="commission-group-name" /></div>
            <div><Label>Description</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="commission-group-description" /></div>
            <Button onClick={save} disabled={saving} className="w-full bg-teal hover:bg-teal-dark text-white" data-testid="save-commission-group-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
